import React, { useState, useEffect, useMemo } from 'react';
import { NavLink, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { t, LOCALES } from '../lib/i18n';
import AppFooter from './AppFooter';
import {
  LayoutDashboard, Users, CalendarDays, FileText, Receipt, Activity,
  Megaphone, QrCode, Wallet, LogOut, Globe, Stethoscope, ShieldCheck, KeyRound,
  ClipboardList, UserCog, Settings, Briefcase, FileSignature, Menu, X, Star, Package, CreditCard, GraduationCap, ShoppingBag, Palette, Building2,
  TrendingUp, Hourglass, ClipboardCheck, Eye, Dumbbell, BookOpen, HeartHandshake, Flame, Webhook, Camera, Lock, Trash2, Search, Database, PlaneTakeoff, ChevronDown, Clock4
} from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import HelpButton from './HelpButton';
import AdminNotificationBell from './AdminNotificationBell';
import AppointmentAlarm from './AppointmentAlarm';
import TrialBanner from './TrialBanner';
import ProfileCompletionModal from './ProfileCompletionModal';
import CommandPalette from './CommandPalette';
import SystemStatus from './SystemStatus';

export default function AppLayout() {
  const { user, logout, locale, setLocale } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Patient profile-completion gate. The popup re-opens on EVERY tab change
  // until the profile is complete, so patients can't forget to finish it.
  // We suppress it on the profile page itself (where the patient IS editing).
  const [completeness, setCompleteness] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const reloadCompleteness = () => {
    if (user?.role !== 'patient') { setCompleteness(null); return; }
    api.get('/patients/me/completeness').then(r => setCompleteness(r.data)).catch(()=>{});
  };
  useEffect(() => { reloadCompleteness(); }, [user?.role]);
  useEffect(() => {
    if (user?.role !== 'patient' || !completeness) return;
    // Iter 67 — when admin has disabled the gate (feature_require_complete_profile=false),
    // we don't pop the modal at all. Admin's choice.
    if (completeness.enforce === false) { setShowProfile(false); return; }
    if (completeness.complete) { setShowProfile(false); return; }
    // Don't pop on the profile page itself — the patient is already editing.
    if (location.pathname === '/patient/profile') { setShowProfile(false); return; }
    // Iter 97 — honour "Skip for now" for the current browser session so the
    // modal doesn't keep nagging the patient on every page navigation.
    if (sessionStorage.getItem('profile-completion-skip') === '1') {
      setShowProfile(false);
      return;
    }
    setShowProfile(true);
  }, [completeness, location.pathname, user?.role]);

  const [clinicSettings, setClinicSettings] = useState({});
  useEffect(() => {
    api.get('/clinic/settings').then(r => setClinicSettings(r.data || {})).catch(() => {});
  }, []);

  // Iter 129 (task a extra) — Persistent deletion banner so users who
  // scheduled their deletion see a permanent "X days until permanent loss"
  // banner across every screen until they undo. Silent 404 for users who
  // aren't authenticated / haven't scheduled.
  const [deletionStatus, setDeletionStatus] = useState(null);
  useEffect(() => {
    if (!user) return;
    api.get('/auth/me/deletion-status')
      .then(r => setDeletionStatus(r.data))
      .catch(() => {});
  }, [user, location.pathname]);
  const cancelDeletion = async () => {
    try {
      await api.post('/auth/me/undelete-account');
      const r = await api.get('/auth/me/deletion-status');
      setDeletionStatus(r.data);
    } catch (_) { /* ignore */ }
  };

  const isAdmin = user?.role === 'admin';
  const isReception = user?.role === 'reception';
  const receptionCan = (key) => clinicSettings[`reception_can_${key}`] !== false;

  // Reception users get a tailored subset of the admin sidebar based on per-area toggles.
  const receptionNav = [
    { to: '/admin', label: t(locale, 'dashboard'), icon: LayoutDashboard, end: true },
    { to: '/admin/patients', label: t(locale, 'patients'), icon: Users },
    { to: '/admin/calendar', label: t(locale, 'appointments'), icon: CalendarDays },
    { to: '/admin/waitlist', label: 'Waitlist', icon: Hourglass },
    { to: '/admin/services', label: 'Services', icon: Briefcase },
    ...(receptionCan('finances') && clinicSettings.feature_payments_module !== false   ? [{ to: '/admin/finances', label: t(locale, 'finances'), icon: Wallet }] : []),
    ...(receptionCan('inventory')  ? [{ to: '/admin/inventory', label: 'Inventory', icon: Package }] : []),
    ...(receptionCan('memberships') && clinicSettings.feature_memberships !== false
        ? [{ to: '/admin/memberships', label: 'Memberships', icon: CreditCard }] : []),
    ...(receptionCan('marketing')  ? [{ to: '/admin/marketing', label: t(locale, 'marketing'), icon: Megaphone }] : []),
    ...(clinicSettings.feature_storefront !== false ? [{ to: '/admin/store', label: 'Product Catalogue', icon: ShoppingBag }] : []),
    { to: '/admin/reports', label: 'Reports', icon: FileText },
    ...(receptionCan('clinical')   ? [{ to: '/admin/exercises', label: t(locale, 'exercises'), icon: Activity }] : []),
  ];

  // Iter 121 — helpers for tier-based sidebar gating. Reads the resolved
  // feature set from /clinic/settings (`_plan_features` is a sorted list of
  // slugs unlocked by the clinic's tier). If backend hasn't sent the flag,
  // fall back to showing the item (safe default — no regression).
  const planFeatures = clinicSettings._plan_features || [];
  const planQuotaFeatures = clinicSettings._plan_quota_features || {};
  const hasPlanFeature = (slug) => planFeatures.length === 0 || planFeatures.includes(slug);
  const hasQuotaFeature = (key) => {
    if (Object.keys(planQuotaFeatures).length === 0) return true;
    return !!planQuotaFeatures[key];
  };

  const adminNav = [
    // Top-level (always visible)
    { to: '/admin', label: t(locale, 'dashboard'), icon: LayoutDashboard, end: true },
    { to: '/admin/patients', label: t(locale, 'patients'), icon: Users },
    { to: '/admin/calendar', label: t(locale, 'appointments'), icon: CalendarDays },
    { to: '/admin/services', label: 'Services', icon: Briefcase },
    ...(clinicSettings.feature_clinician_profiles !== false ? [{ to: '/admin/clinicians', label: 'Clinicians', icon: Stethoscope }] : []),
    { to: '/admin/clinician-hours', label: 'Clinician hours', icon: Clock4 },
    { to: '/admin/time-off', label: 'Time-off approvals', icon: PlaneTakeoff },
    ...(clinicSettings.feature_payments_module !== false ? [{ to: '/admin/finances', label: t(locale, 'finances'), icon: Wallet }] : []),
    // Iter 121 — team_analytics is medium+ only. Solo tier hides KPIs.
    ...(hasQuotaFeature('team_analytics') ? [{ to: '/admin/kpis', label: 'Key Performance Indicator', icon: TrendingUp }] : []),
    { to: '/admin/waitlist', label: 'Waitlist', icon: Hourglass },
    { to: '/admin/exercises', label: t(locale, 'exercises'), icon: Activity },
    // Iter 121 — marketing_automation is medium+ only.
    ...(hasQuotaFeature('marketing_automation') && hasPlanFeature('marketing') ? [{ to: '/admin/marketing', label: t(locale, 'marketing'), icon: Megaphone }] : []),
    { to: '/admin/reviews', label: 'Reviews', icon: Star },
    { to: '/admin/invite', label: t(locale, 'invite'), icon: QrCode },
    { to: '/admin/users', label: 'Users', icon: UserCog },
    ...(clinicSettings.feature_hr !== false ? [{ to: '/admin/hr', label: 'HR', icon: FileSignature }] : []),
    { to: '/admin/inventory', label: 'Inventory', icon: Package },
    ...(clinicSettings.feature_memberships !== false ? [{ to: '/admin/memberships', label: 'Memberships', icon: CreditCard }] : []),
    { to: '/admin/treatment-packages', label: 'Treatment packages', icon: Package },
    { to: '/admin/hep', label: 'HEP video library', icon: Activity },
    // Iter 133 — Education library ("Watch & learn") removed from admin sidebar
    // per user request. Same route exists for legacy deep-links.
    // Iter 121 — storefront is medium+ only.
    ...(hasPlanFeature('storefront') && clinicSettings.feature_storefront !== false ? [{ to: '/admin/store', label: 'Product Catalogue', icon: ShoppingBag }] : []),
    // Iter 121 — student_tracker is medium+ only.
    ...(hasPlanFeature('student_tracker') && clinicSettings.feature_student_tracker !== false ? [{ to: '/admin/students', label: 'Students', icon: GraduationCap }] : []),
    // Iter 131 — Fitness is a paid add-on. Platform admins ALWAYS see the
    // nav (they need cross-tenant access for support). Clinic admins only
    // see it when the add-on is active on their tenant. Everyone else sees
    // the CTA card inside AdminFitness/FitnessDisabled.
    ...((clinicSettings.feature_fitness || user?.role === 'platform_admin')
      ? [{ to: '/admin/fitness', label: 'Fitness & wellness', icon: Dumbbell }]
      : []),
    ...(clinicSettings.feature_ops_manual !== false ? [{ to: '/admin/operations-manual', label: 'Daily operations manual', icon: BookOpen }] : []),
    ...(clinicSettings.feature_setup_manual !== false ? [{ to: '/admin/setup-manual', label: 'Clinic setup manual', icon: BookOpen }] : []),
    { to: '/admin/reports', label: 'Reports', icon: FileText },
    { to: '/admin/wave5', label: 'Integrations & analytics', icon: Webhook },
    { to: '/admin/posture-capture', label: 'Posture capture (beta)', icon: Camera },
    ...(clinicSettings.feature_seasonal_theme !== false ? [{ to: '/admin/themes', label: 'Themes', icon: Palette }] : []),
    { to: '/admin/brand-preview', label: 'Brand preview', icon: Eye },
    { to: '/admin/audit', label: 'Audit log', icon: ShieldCheck },
    { to: '/admin/data-migration', label: 'Data migration', icon: Database },
    { to: '/admin/compliance', label: 'Compliance', icon: ShieldCheck },
    { to: '/admin/security', label: '2FA', icon: KeyRound },
    { to: '/admin/system-status', label: 'System status', icon: Activity },
    ...(user?.is_owner ? [
      { to: '/admin/billing', label: 'Billing & subscription', icon: CreditCard },
    ] : []),
    { to: '/admin/settings', label: 'Settings', icon: Settings },
  ];

  // Iter 130 — Grouped view of adminNav by role/function. Reduces the ~30-item
  // flat sidebar into ~5 collapsible sections + a few always-pinned items.
  // Each group persists its open/closed state in localStorage. Direct-link
  // navigation to any item auto-opens its group so the active item is visible.
  const adminNavGrouped = useMemo(() => {
    if (user?.role !== 'admin') return null;
    const byPath = Object.fromEntries(adminNav.map(n => [n.to, n]));
    const only = (paths) => paths.map(p => byPath[p]).filter(Boolean);
    return [
      // Pinned quick-access (never collapsed).
      { id: 'pinned', label: '', pinned: true, items: only([
        '/admin', '/admin/patients', '/admin/calendar',
      ]) },
      // Clinical operations
      { id: 'clinical', label: 'Clinical', icon: Stethoscope, items: only([
        '/admin/services', '/admin/clinicians', '/admin/clinician-hours', '/admin/time-off',
        '/admin/waitlist', '/admin/exercises', '/admin/treatment-packages',
        '/admin/hep', '/admin/posture-capture',
      ]) },
      // Front-office / people
      { id: 'front-office', label: 'Front office', icon: UserCog, items: only([
        '/admin/reviews', '/admin/invite', '/admin/users', '/admin/hr',
        ...((clinicSettings.feature_fitness || user?.role === 'platform_admin') ? ['/admin/fitness'] : []),
        ...(clinicSettings.feature_student_tracker !== false ? ['/admin/students'] : []),
      ]) },
      // Financials & business
      { id: 'business', label: 'Business', icon: Wallet, items: only([
        '/admin/finances', '/admin/kpis', '/admin/marketing',
        '/admin/inventory', '/admin/memberships', '/admin/store',
        '/admin/reports',
      ]) },
      // Documentation & content
      { id: 'documentation', label: 'Documentation', icon: BookOpen, items: only([
        '/admin/operations-manual', '/admin/setup-manual',
        '/admin/brand-preview', '/admin/themes',
      ]) },
      // Governance / security
      { id: 'governance', label: 'Governance', icon: ShieldCheck, items: only([
        '/admin/audit', '/admin/data-migration', '/admin/compliance',
        '/admin/security', '/admin/wave5', '/admin/system-status',
      ]) },
      // Owner-only account section (Billing + Settings always at the bottom).
      { id: 'account', label: 'Account', icon: Settings, items: only([
        ...(user?.is_owner ? ['/admin/billing'] : []),
        '/admin/settings',
      ]) },
    ].filter(g => g.items.length > 0);
  }, [user?.role, user?.is_owner, JSON.stringify(clinicSettings), locale]);

  // Iter 106 — Platform-admin nav lives in its OWN pinned section at the top
  // of the sidebar (not buried inside adminNav). Hidden for everyone else.
  const platformNav = user?.role === 'platform_admin' ? [
    { to: '/platform', label: 'Platform home', icon: LayoutDashboard, end: true },
    { to: '/platform/clinics', label: 'Clinics', icon: Building2 },
    { to: '/platform/subscribers', label: 'Subscribers', icon: Users },
    { to: '/platform/revenue', label: 'Revenue', icon: TrendingUp },
    { to: '/platform/users', label: 'Purge user', icon: Trash2 },
  ] : [];
  // Patient state-aware sidebar visibility — hides clinical pages until they're meaningful.
  // - Interview only shows once the patient has at least one appointment booked.
  // - Treatment plan only shows once the therapist has saved an objective assessment.
  const [patientState, setPatientState] = useState({ hasAppointment: false, hasObjective: false });
  useEffect(() => {
    if (user?.role !== 'patient') return;
    let alive = true;
    (async () => {
      try {
        const [appts, obj] = await Promise.allSettled([
          api.get('/appointments'),
          api.get(`/objective/${user.user_id}`),
        ]);
        if (!alive) return;
        setPatientState({
          hasAppointment: appts.status === 'fulfilled' && (appts.value.data || []).length > 0,
          hasObjective: obj.status === 'fulfilled' && !!(obj.value.data && (obj.value.data.muscle_tests?.length || obj.value.data.diagnosis_text)),
        });
      } catch (_) {}
    })();
    return () => { alive = false; };
  }, [user?.role, user?.user_id]);

  const patientNav = [
    { to: '/patient', label: t(locale, 'dashboard'), icon: LayoutDashboard, end: true },
    // Iter 133 — Profile bubbled up per user request (was at the very bottom
    // of the flat list — patients complained they couldn't find it).
    { to: '/patient/profile', label: t(locale, 'profile'), icon: ShieldCheck },
    { to: '/patient/book', label: 'Book session', icon: CalendarDays },
    { to: '/patient/appointments', label: t(locale, 'appointments'), icon: ClipboardList },
    // Intake only appears once the patient has booked at least one appointment.
    ...(patientState.hasAppointment ? [{ to: '/patient/interview', label: 'Interview', icon: ClipboardList }] : []),
    // Iter 133 — Confidential Medical Documents standalone page removed from
    // the sidebar. Docs are still uploaded from Part 1 interview + tied to
    // the patient's record; we just don't surface the browser as a separate
    // menu item any more (kept the route for legacy deep-links).
    // Iter 132 — Exercise library is clinician-only. Patients see only their
    // assigned exercises through the HEP path below.
    { to: '/patient/hep', label: 'My exercises', icon: Dumbbell },
    // Iter 133 — "Watch & learn" education library removed from the sidebar
    // per user request (still accessible from clinician-assigned links).
    // Iter 132 — Outcome measures + Recovery surveys are gated behind an
    // admin-controlled toggle. When OFF (default), only clinicians see them
    // and can assign specific surveys to patients. When ON, patients see
    // the full self-serve inbox.
    ...(clinicSettings.feature_outcome_measures_patient_visible ? [
      { to: '/patient/outcomes', label: 'Outcome measures', icon: ClipboardCheck },
      { to: '/patient/proms', label: 'Recovery surveys', icon: ClipboardList },
    ] : []),
    // Iter 133 — HEP progress + My progress combined into a single "Rehab
    // progress" page (see RehabProgress.jsx). Both routes still resolve to
    // the same page so old bookmarks don't break.
    { to: '/patient/rehab-progress', label: 'Rehab progress', icon: TrendingUp },
    { to: '/patient/membership', label: 'My membership', icon: CreditCard },
    ...(clinicSettings.feature_fitness ? [{ to: '/patient/fitness', label: 'Fitness & wellness', icon: Dumbbell }] : []),
    { to: '/patient/invoices', label: t(locale, 'invoices'), icon: Receipt },
    { to: '/patient/account', label: t(locale, 'account_privacy'), icon: KeyRound },
    { to: '/patient/trust', label: 'Trust profile', icon: Lock },
  ];

  // Clinician sees the same staff dashboard but with sensitive admin-only items removed.
  // Iter 72 — calendar/appointments/invoices removed from clinician sidebar (user request).
  // Iter 116 — My availability panel (weekly schedule + time-off + buffer).
  // Iter 133 — Profile bubbled up + Watch & learn removed.
  const clinicianNav = [
    { to: '/admin', label: t(locale, 'dashboard'), icon: LayoutDashboard, end: true },
    { to: '/admin/patients', label: t(locale, 'patients'), icon: Users },
    { to: '/admin/exercises', label: t(locale, 'exercises'), icon: Activity },
    { to: '/staff/my-availability', label: 'My availability', icon: CalendarDays },
    { to: '/admin/security', label: '2FA', icon: KeyRound },
  ];

  const nav = user?.role === 'admin' ? adminNav
            : user?.role === 'reception' ? receptionNav
            : user?.role === 'clinician' ? clinicianNav
            : patientNav;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Iter 115 — Branding fallback: once a clinic has custom branding
  // (_has_custom_branding is set by /clinic/settings when the tenant doc
  // stores both logo + tagline), we show ONLY their fields — no more
  // "Physiogenic · Recover · Rehab · Train" defaults sneaking into their
  // sidebar. Fully un-branded clinics still see "Physiogenic" as the
  // platform-name fallback (they should never reach this state in
  // production because /onboard/branding is required for new signups).
  const hasCustomBranding = !!clinicSettings._has_custom_branding;
  // Iter 119 — Backend already returns clinic_name = "Physiogenic" for
  // the platform-default clinic (clinic_id="default") AND all subscribers
  // set clinic_name during signup. So the resolved settings.clinic_name
  // is ALWAYS a real value; we don't need an inline `|| 'Physiogenic'`
  // fallback. Removing that literal was critical — the visual-edits Babel
  // plugin was freezing the JSX child to the literal string, ignoring
  // the runtime value from state.
  const displayName = clinicSettings.clinic_name || '';
  const displayTagline = hasCustomBranding ? (clinicSettings.clinic_tagline || '') : '';
  // Iter 119 — build the footer copyright entirely from dynamic pieces
  // (no static-string prefix concatenation) so the visual-edits Babel
  // plugin can't detect a static string and substitute it in place of
  // the dynamic result.
  const footerCopyright = useMemo(() => {
    if (!displayName) return '';
    const cop = String.fromCharCode(169);
    const yr = ['2024', String.fromCharCode(0x2013), '2026'].join('');
    return [cop, yr, displayName].join(' ');
  }, [displayName]);
  const isPlatformDefault = clinicSettings._clinic_id === 'default';

  const Sidebar = (
    <aside className="w-64 surface border-r flex flex-col h-full" data-testid="app-sidebar">
      <div className="p-6 border-b border-soft flex items-center gap-3">
        <BrandLogo size={72} src={clinicSettings.clinic_logo_url || undefined} />
        <div className="min-w-0">
          {displayName && (
            <div className="font-heading font-bold tracking-tight text-fg leading-tight truncate" data-testid="sidebar-clinic-name">
              {displayName}
            </div>
          )}
          {displayTagline && (
            <div className="text-[10px] text-fg-muted -mt-0.5 uppercase tracking-wider truncate" data-testid="sidebar-clinic-tagline">
              {displayTagline}
            </div>
          )}
        </div>
        <button onClick={()=>setMobileOpen(false)} className="ml-auto md:hidden text-fg-muted"
                data-testid="close-sidebar">
          <X className="w-5 h-5"/>
        </button>
      </div>
      <nav className="p-3 flex-1 overflow-auto space-y-1">
        {/* Iter 106 — Pinned PLATFORM ADMIN section at the top, only visible
            for platform_admin users. Distinct red accent so they're clearly
            destructive / SaaS-operator tools and not part of clinic ops. */}
        {platformNav.length > 0 && (
          <div className="mb-3 pb-3 border-b border-soft" data-testid="platform-nav-section">
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-[#B83A3A] font-bold">
              Platform admin
            </div>
            {platformNav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-platform-${n.to.split('/').pop() || 'home'}`}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                    isActive
                      ? 'bg-[#FCE6E6] text-[#B83A3A] font-medium'
                      : 'text-[#4A524A] hover:bg-[#FCE6E6]'
                  }`
                }
              >
                <n.icon className="w-4 h-4" strokeWidth={1.6} />
                {n.label}
              </NavLink>
            ))}
          </div>
        )}
        {/* Trial countdown — only meaningful for SaaS admin tenants. The
            component handles its own visibility (hidden once Stripe is
            connected, or for legacy/default tenant). */}
        {user?.role === 'admin' && <TrialBanner/>}
        {/* Iter 130 — Grouped admin sidebar. Collapsible sections with
            localStorage persistence + auto-open of the group that owns the
            currently-active route so users always see where they are. Other
            roles (patient/clinician/reception) keep the compact flat list. */}
        {user?.role === 'admin' && adminNavGrouped ? (
          adminNavGrouped.map(g => (
            <NavGroup
              key={g.id}
              group={g}
              currentPath={location.pathname}
              onNavigate={() => setMobileOpen(false)}
            />
          ))
        ) : (
          nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.to.split('/').pop() || 'dashboard'}`}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  isActive
                    ? 'bg-[#F2EBDB] text-[#003D80] font-medium'
                    : 'text-[#4A524A] hover:bg-[#F2EBDB]'
                }`
              }
            >
              <n.icon className="w-4 h-4" strokeWidth={1.6} />
              {n.label}
            </NavLink>
          ))
        )}
      </nav>
      <div className="p-3 border-t border-soft space-y-3">
        {/* Iter 107 — ⌘K palette discoverability. Clicking simulates the
            shortcut by dispatching the key event the palette listens for.
            Iter 133 — Quick jump: colored background chip so the trigger
            stands out from the flat sidebar palette (users reported it
            was hard to read as plain-text). */}
        <button
          type="button"
          onClick={() => {
            const isMac = /Mac/i.test(navigator.platform);
            window.dispatchEvent(new KeyboardEvent('keydown', {
              key: 'k', ctrlKey: !isMac, metaKey: isMac, bubbles: true,
            }));
          }}
          className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-[#003D80] bg-[#E8F0FA] hover:bg-[#D9E5F5] border border-[#B4C4DA] rounded-md transition"
          data-testid="cmdk-hint">
          <Search className="w-3.5 h-3.5"/>
          <span className="flex-1 text-left">Quick jump…</span>
          <kbd className="font-mono text-[10px] bg-white/80 border border-[#B4C4DA]/60 px-1.5 py-0.5 rounded">⌘K</kbd>
        </button>
        {user?.role === 'admin' && <AdminNotificationBell />}
        <div className="flex items-center gap-2 px-2">
          <Globe className="w-4 h-4 text-fg-muted" />
          <select
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            data-testid="locale-select"
            className="text-xs bg-transparent border border-soft rounded px-2 py-1 outline-none"
          >
            {LOCALES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </div>
        <div className="px-2">
          <div className="text-xs font-medium text-fg">{user?.name}</div>
          <div className="text-[11px] text-fg-muted truncate">{user?.email}</div>
          <div className="text-[10px] mt-1 text-brand uppercase tracking-wider">{user?.role}</div>
        </div>
        <select
          data-testid="sidebar-locale"
          aria-label="Language"
          data-no-translate
          value={locale}
          onChange={(e) => setLocale(e.target.value)}
          className="text-xs surface border border-soft rounded-md px-2 py-1.5 w-full"
        >
          <option value="en">EN — English</option>
          <option value="pl">PL — Polski</option>
          <option value="es">ES — Español</option>
        </select>
        {/* Iter 122 — System status chip: 60 s /api/health poll so admins
            immediately spot backend hiccups (Cloudflare 502, cold-start, etc)
            without opening the browser dev-tools.
            Iter 133 — Restricted to admin + platform_admin — clinicians and
            patients don't need the operator view. */}
        {(user?.role === 'admin' || user?.role === 'platform_admin') && <SystemStatus />}
        <button
          data-testid="logout-button"
          onClick={handleLogout}
          className="flex items-center gap-2 px-2 py-2 text-sm text-[#B83A3A] hover:bg-[#F2EBDB] rounded-md w-full"
        >
          <LogOut className="w-4 h-4" /> {t(locale, 'nav_logout')}
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex section">
      {/* Desktop sidebar */}
      <div className="hidden md:block">{Sidebar}</div>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={()=>setMobileOpen(false)} />
          <div className="relative h-full w-64 z-50">{Sidebar}</div>
        </div>
      )}

      <main className="flex-1 overflow-auto">
        {/* Iter 129 — Site-wide deletion banner (dismiss = /patient/account or /account/security). */}
        {deletionStatus?.scheduled && (
          <div
            className="sticky top-0 z-40 border-b-2 border-red-400 bg-red-50 px-4 py-2.5 flex items-center justify-between gap-3 shadow-sm"
            data-testid="site-deletion-banner"
          >
            <div className="flex items-center gap-2.5 text-xs sm:text-sm text-red-900 min-w-0">
              <span className="inline-flex w-6 h-6 rounded-full bg-red-600 text-white items-center justify-center shrink-0 font-bold">!</span>
              <span className="truncate">
                Your account will be <strong>permanently deleted</strong> on{' '}
                <strong>{deletionStatus.scheduled_for?.slice(0, 10)}</strong>
                {' '}({deletionStatus.days_remaining} day{deletionStatus.days_remaining === 1 ? '' : 's'} left).
              </span>
            </div>
            <button
              type="button"
              onClick={cancelDeletion}
              className="shrink-0 text-xs font-semibold px-3 py-1 rounded-md bg-red-600 text-white hover:bg-red-700 transition"
              data-testid="site-deletion-banner-cancel"
            >
              Cancel deletion
            </button>
          </div>
        )}
        {/* Mobile top bar */}
        <div className="md:hidden sticky top-0 z-30 surface border-b border-soft flex items-center justify-between px-4 py-3" data-testid="mobile-header">
          <button onClick={()=>setMobileOpen(true)} className="text-fg" data-testid="open-sidebar">
            <Menu className="w-6 h-6"/>
          </button>
          <div className="flex items-center gap-2">
            <BrandLogo size={52} src={clinicSettings.clinic_logo_url || undefined} />
            {displayName && (
              <span className="font-heading font-bold text-sm truncate" data-testid="mobile-clinic-name">
                {displayName}
              </span>
            )}
          </div>
          <div className="w-6" />
        </div>
        <Outlet />
        <HelpButton />
        <AppointmentAlarm />
        {user?.role === 'patient' && <ConsentReprompt/>}
        {user?.role === 'patient' && (
          <ProfileCompletionModal
            open={showProfile}
            onClose={() => setShowProfile(false)}
            onSaved={() => { reloadCompleteness(); }}
          />
        )}
        {/* Iter 75 — universal app footer with copyright + version.
            Iter 119 — AppLayout composes the full copyright string here
            using the resolved clinic_name (backend returns "Physiogenic"
            for the platform-default tenant AND the actual clinic name
            for subscribers). No inline 'Physiogenic' literal — the
            visual-edits Babel plugin freezes any JSX prop whose source
            expression contains a static string fallback.
            The /legal/copyright link only surfaces on the platform-
            default clinic (clinic_id="default"), not on branded
            subscribers who don't want to link out to Physiogenic's
            copyright page.*/}
        <AppFooter
          copyrightText={footerCopyright}
          showBrandLegalLink={isPlatformDefault}
        />
      </main>
      {/* Iter 107 — global ⌘K command palette. Mounted once, listens
          globally for the shortcut. */}
      <CommandPalette user={user}/>
    </div>
  );
}

/**
 * Iter 130 — Sidebar navigation group.
 *
 * Renders a collapsible section (via native <details>) for grouped admin
 * nav items. Persists open/closed state in localStorage keyed by group id,
 * and auto-opens the group that owns the currently-active route so the
 * user is never confused about their location.
 *
 * Pinned groups (group.pinned === true) render as a flat list without a
 * <summary> — used for the "Dashboard / Patients / Calendar" trio at the
 * very top.
 */
function NavGroup({ group, currentPath, onNavigate }) {
  const containsActive = group.items.some(
    n => n.end
      ? currentPath === n.to
      : (currentPath === n.to || currentPath.startsWith(n.to + '/'))
  );
  const lsKey = `sidebar:group:${group.id}`;
  const [open, setOpen] = React.useState(() => {
    if (containsActive) return true;
    try {
      const v = localStorage.getItem(lsKey);
      if (v === '1') return true;
      if (v === '0') return false;
    } catch (_) { /* ignore */ }
    // Sensible defaults: Clinical open, Business/Docs/Governance closed.
    return group.id === 'clinical' || group.id === 'account';
  });
  React.useEffect(() => {
    if (containsActive && !open) setOpen(true);
  }, [containsActive]);
  const onToggle = (e) => {
    const isOpen = !!e.target.open;
    setOpen(isOpen);
    try { localStorage.setItem(lsKey, isOpen ? '1' : '0'); } catch (_) { /* ignore */ }
  };

  const renderLink = (n) => (
    <NavLink
      key={n.to}
      to={n.to}
      end={n.end}
      data-testid={`nav-${n.to.split('/').pop() || 'dashboard'}`}
      onClick={onNavigate}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
          isActive
            ? 'bg-[#F2EBDB] text-[#003D80] font-medium'
            : 'text-[#4A524A] hover:bg-[#F2EBDB]'
        }`
      }
    >
      <n.icon className="w-4 h-4" strokeWidth={1.6} />
      {n.label}
    </NavLink>
  );

  // Pinned group renders flat (no dropdown).
  if (group.pinned) {
    return (
      <div className="space-y-1 mb-1" data-testid={`nav-group-${group.id}`}>
        {group.items.map(renderLink)}
      </div>
    );
  }

  return (
    <details
      open={open}
      onToggle={onToggle}
      className="group/navgroup"
      data-testid={`nav-group-${group.id}`}
    >
      <summary
        className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm cursor-pointer select-none transition-colors marker:hidden list-none ${
          containsActive
            ? 'text-[#003D80] font-semibold hover:bg-[#F2EBDB]'
            : 'text-[#4A524A] hover:bg-[#F2EBDB]'
        }`}
        data-testid={`nav-group-summary-${group.id}`}
      >
        {group.icon && <group.icon className="w-4 h-4 shrink-0" strokeWidth={1.6} />}
        <span className="flex-1 uppercase tracking-[0.08em] text-[11px]">
          {group.label}
        </span>
        <ChevronDown
          className="w-3.5 h-3.5 shrink-0 text-fg-muted transition-transform group-open/navgroup:rotate-180"
          strokeWidth={1.8}
        />
      </summary>
      <div className="pl-2 mt-0.5 space-y-0.5 border-l-2 border-[#F2EBDB]/70 ml-3">
        {group.items.map(renderLink)}
      </div>
    </details>
  );
}

/**
 * Iter 96 — Consent re-prompt banner.
 *
 * Fires once per CURRENT_VERSION bump. Reads /auth/me/consents — if any row
 * has `needs_reconsent: true`, show a non-dismissable banner asking the
 * patient to re-review their privacy preferences.
 */
function ConsentReprompt() {
  const navigate = useNavigate();
  const location = useLocation();
  const [needsReconsent, setNeedsReconsent] = useState(false);
  // Iter 133 — Re-check on every route change so the banner disappears the
  // moment the patient updates their consents on /patient/account.
  useEffect(() => {
    // Session-scoped dismiss lets patients hide the banner for the current
    // browser tab without touching their consent state.
    if (sessionStorage.getItem('consent_reprompt_dismissed') === '1') {
      setNeedsReconsent(false);
      return;
    }
    api.get('/auth/me/consents')
      .then(r => {
        const any = (r.data.consents || []).some(c => c.needs_reconsent);
        setNeedsReconsent(any);
      })
      .catch(() => {});
  }, [location.pathname]);
  const dismiss = () => {
    try { sessionStorage.setItem('consent_reprompt_dismissed', '1'); } catch (_) { /* ignore */ }
    setNeedsReconsent(false);
  };
  if (!needsReconsent) return null;
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 surface border-2 border-[#FFB000] bg-[#FFB000]/10 rounded-lg shadow-lg p-4 max-w-md flex items-start gap-3"
          data-testid="consent-reprompt-banner">
      <div className="text-[#FFB000] text-2xl leading-none">!</div>
      <div className="flex-1">
        <div className="font-semibold text-sm">Our privacy terms have been updated</div>
        <p className="text-xs text-fg-muted mt-0.5">
          Please review your data-use consents to make sure everything still reflects your wishes.
        </p>
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <button onClick={() => { dismiss(); navigate('/patient/account'); }}
                className="btn-primary text-xs px-3 py-1.5"
                data-testid="reconsent-cta">
          Review →
        </button>
        <button onClick={dismiss}
                className="text-[10px] text-fg-muted hover:text-fg underline"
                data-testid="reconsent-dismiss">
          Not now
        </button>
      </div>
    </div>
  );
}

